import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import logger from "../utils/logger";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useSearch, useSearchCallbacks } from "../contexts/SearchContext";
import HelpFooter from "../components/HelpFooter";
import { usePageTracking } from "../hooks/useAnalytics";
import useTwoFingerScroll from "../hooks/useTwoFingerScroll";
import AuthWall from "../components/AuthWall";
import IOSAppBanner from "../components/IOSAppBanner";
import StorySuggestionAlert from "../components/StorySuggestionAlert";
import ContinuePlaybackSection from "../components/ContinuePlaybackSection";
import { motion, useInView, AnimatePresence } from "framer-motion";

/* ─── Pixley Movie — special player / badge treatment ─── */
const FEATURED_SHOW_ID = "6cd7a3a7-fbba-431a-bee4-b39fffd29c43";

/* Newest Original pinned to banner + first in the Originals row */
const NEW_SHOW_ID = "4dc872da-2b2e-4c7b-ac20-fccfe2b46c29"; // The Fading Forest

/* Pixley Originals row: pin new show first, then newest → oldest */
const sortShowsNewestFirst = (showsList) => {
  if (!Array.isArray(showsList) || showsList.length === 0) return [];
  const sorted = [...showsList].sort((a, b) => {
    const ta = new Date(a?.created_at || 0).getTime();
    const tb = new Date(b?.created_at || 0).getTime();
    return tb - ta;
  });
  const idx = sorted.findIndex((s) => s?.id === NEW_SHOW_ID);
  if (idx <= 0) return sorted;
  return [sorted[idx], ...sorted.slice(0, idx), ...sorted.slice(idx + 1)];
};

/* ─── animation presets ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

function AnimatedSection({ children, className, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── shared scrollable row ─── */
const ScrollRow = ({ children, className }) => {
  const ref = useRef(null);
  useTwoFingerScroll(ref);
  return (
    <div ref={ref} className={`sv-scroll-row ${className || ""}`}>
      {children}
    </div>
  );
};

/* ─── floating CTA for unauthenticated users ─── */
function FloatingCta({ onSignup }) {
  return (
    <div className="sv-floating-cta">
      <motion.button
        type="button"
        onClick={onSignup}
        className="sv-cta-pill"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -2 }}
        whileTap={{ y: 2 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0065F8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
        <span>Create your own story — It's free!</span>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
      </motion.button>
    </div>
  );
}

/* ── Hero carousel — isolated component, own state, doesn't re-render parent ── */
const HeroCarousel = React.memo(({ items, onStoryClick, onShowClick, getThumb }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;

  const activeItem = items[idx] || items[0];
  const isNewShow = activeItem.id === NEW_SHOW_ID;
  const isFeaturedMovie = activeItem.id === FEATURED_SHOW_ID;

  return (
    <div className="sv-hero">
      {items.map((item, i) => {
        const isStory = item._type === "story";
        const thumb = isStory ? item.thumbnail_url : getThumb(item);
        const isVid = !isStory && thumb !== item.cover_image_url && thumb?.includes(".mp4");
        const active = i === idx;

        return (
          <div key={item.id} className="sv-hero-bg"
            style={{ opacity: active ? 1 : 0, transition: "opacity 0.8s ease-in-out", position: i === 0 ? "relative" : "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: active ? 1 : 0 }}>
            {isVid ? (
              <video src={thumb} className="sv-hero-media" muted loop autoPlay playsInline preload="none" poster={item.cover_image_url || ""}
                onLoadedMetadata={(e) => { if (e.target.duration > 0) e.target.currentTime = e.target.duration / 2; }} />
            ) : (
              <img src={thumb} alt={item.name} className="sv-hero-media" loading={i === 0 ? "eager" : "lazy"}
                onError={(e) => { e.target.src = "/assets/pixley-panda-nobg.png"; }} />
            )}
          </div>
        );
      })}
      <div className="sv-hero-vignette" />
      <div className="sv-hero-bottom">
        <div>
          {isNewShow && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 999, background: "rgba(0,101,248,0.88)", border: "2px solid rgba(255,255,255,0.45)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", marginBottom: 10, fontSize: 12, fontWeight: 700, fontFamily: "'Fredoka', sans-serif", color: "#FFFFFF", letterSpacing: "0.06em", textTransform: "uppercase", textShadow: "0 1px 4px rgba(11,26,51,0.5)", boxShadow: "0 4px 0 rgba(7,59,142,0.55)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
              New Show!
            </div>
          )}
          {isFeaturedMovie && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 999, background: "rgba(11,26,51,0.38)", border: "2px solid rgba(255,255,255,0.35)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", marginBottom: 10, fontSize: 12, fontWeight: 700, fontFamily: "'Fredoka', sans-serif", color: "#FFFFFF", letterSpacing: "0.06em", textTransform: "uppercase", textShadow: "0 1px 4px rgba(11,26,51,0.5)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}>movie</span>
              Pixley Original Movie
            </div>
          )}
          <h1 className="sv-hero-title">{activeItem.name}</h1>
          <button className="sv-hero-play"
            onClick={() => {
              activeItem._type === "story" ? onStoryClick(activeItem.id) : onShowClick(activeItem.id);
            }}>
            <svg viewBox="0 0 24 24" fill="#0065F8" width="18" height="18"><path d="M8 5v14l11-7z" /></svg>
            Play
          </button>
        </div>
        {items.length > 1 && (
          <div className="sv-hero-indicators">
            {items.map((_, i) => (
              <button key={i} className={`sv-dot${i === idx ? " on" : ""}`} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
const Storyverse = () => {
  usePageTracking("Storyverse");
  const { apiClient } = useAPI();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { searchQuery } = useSearch();
  const { registerSearchCallbacks, unregisterSearchCallbacks } = useSearchCallbacks();

  const [shows, setShows] = useState([]);
  const [stories, setStories] = useState([]);
  const [publicStorybooks, setPublicStorybooks] = useState([]);
  const [originalStories, setOriginalStories] = useState([]);
  const [forYouStories, setForYouStories] = useState([]);
  const [, setForYouStorybooks] = useState([]);
  const [forYouLoading, setForYouLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuthWall, setShowAuthWall] = useState(false);
  const [authWallFeature, setAuthWallFeature] = useState("content");
  const [showSuggestionAlert, setShowSuggestionAlert] = useState(false);
  const [pixleyMixes, setPixleyMixes] = useState({ top_views: [], adventure: [], mystery: [], my_episodes: [] });
  const [mixesLoading, setMixesLoading] = useState(false);

  /* ── data loading ── */
  const origRef = useRef(originalStories);
  const apiRef = useRef(apiClient);
  const queryRef = useRef(searchQuery);
  origRef.current = originalStories;
  queryRef.current = searchQuery;
  apiRef.current = apiClient;

  // Tier 1: Core content — fast APIs, blocks render
  const loadStoriverseData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [showsR, storiesR, booksR] = await Promise.all([
        apiClient.getStoriverseShows(),
        apiClient.getStoriverseStories(),
        apiClient.getPublicStorybooksFeed(10, 0),
      ]);
      if (!showsR.status) throw new Error(showsR.error || "Failed to load");
      setShows(sortShowsNewestFirst(showsR.data || []));
      if (storiesR.status) { const d = storiesR.data || []; setOriginalStories(d); origRef.current = d; if (!queryRef.current) setStories(d); }
      if (booksR?.status) { setPublicStorybooks((Array.isArray(booksR.data) ? booksR.data : []).filter((b) => b?.is_public)); } else setPublicStorybooks([]);
    } catch (err) { logger.error("loadStoriverseData error:", err); setError(err.message); setPublicStorybooks([]); }
    finally { setLoading(false); }
  }, [apiClient]);

  // Tier 2: Secondary content — loads after page is visible, fills in when ready
  useEffect(() => {
    if (!apiClient || loading) return;
    // Mixes
    setMixesLoading(true);
    apiClient.getPixleyMixes().then((response) => {
      const emptyMixes = { top_views: [], adventure: [], mystery: [], my_episodes: [] };
      const raw = response?.data ?? response;
      const keys = ["top_views", "adventure", "mystery", "my_episodes"];
      let out = { ...emptyMixes };
      if (Array.isArray(raw) && raw.length) {
        raw.forEach((item) => { if (item?.key && keys.includes(item.key) && Array.isArray(item.value)) out[item.key] = item.value; });
      } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        keys.forEach((k) => { out[k] = Array.isArray(raw[k]) ? raw[k] : []; });
      }
      const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };
      out.adventure = shuffle(out.adventure);
      out.mystery = shuffle(out.mystery);
      setPixleyMixes(out);
    }).catch((err) => logger.error("Pixley Mixes error:", err))
      .finally(() => setMixesLoading(false));
    // For You
    if (isAuthenticated) {
      setForYouLoading(true);
      apiClient.getForYouStories().then((r) => {
        if (r?.status && r.data) { setForYouStories(r.data.stories || []); setForYouStorybooks(r.data.storybooks || []); }
      }).catch(() => {}).finally(() => setForYouLoading(false));
    }
  }, [apiClient, loading, isAuthenticated]);

  useEffect(() => { loadStoriverseData(); }, [loadStoriverseData]);
  useEffect(() => { const s = sessionStorage.getItem("showStorySuggestion"); if (s === "true") { setShowSuggestionAlert(true); sessionStorage.removeItem("showStorySuggestion"); } }, []);

  useEffect(() => {
    registerSearchCallbacks(
      async (q) => { if (!q?.trim()) { setStories(origRef.current); return; } try { const r = await apiRef.current.searchStoriesByEmbeddings(q, 10); if (r.status) setStories(Array.isArray(r.stories) ? r.stories : []); } catch (e) { logger.error("Search error:", e); } },
      () => setStories(origRef.current)
    );
    return () => unregisterSearchCallbacks();
  }, [registerSearchCallbacks, unregisterSearchCallbacks]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── actions ── */
  const handleShowClick = (id) => {
    if (!isAuthenticated) { setAuthWallFeature("storyverse_original"); setShowAuthWall(true); return; }
    // Featured show goes straight to player (no episode list)
    if (id === FEATURED_SHOW_ID) {
      navigate(`/storyverse/standalone/${id}`);
    } else {
      navigate(`/storyverse/shows/${id}`);
    }
  };
  const handleStoryClick = async (id, shareToken = null) => {
    if (window.gtag) window.gtag("event", "episode_view", { story_id: id });
    try { await apiClient.incrementStoryView(id); } catch (_) {}
    sessionStorage.setItem("autoplay_video_story", "true");
    // Public feed stories are token-gated now — carry the share_token so the
    // detail page can open a story the viewer doesn't own.
    navigate(`/stories/${id}${shareToken ? `?token=${encodeURIComponent(shareToken)}` : ""}`);
  };
  const getThumb = (show) => {
    if (!show.episodes?.length) return show.cover_image_url;
    const ep = show.episodes.find((e) => e.file_url && e.status === "GENERATED");
    return ep ? ep.file_url : show.cover_image_url;
  };

  /* ── hero items (memoized, passed to isolated Hero component) ── */
  const heroItems = useMemo(() => {
    const all = [...shows.map((s) => ({ ...s, _type: "show" })), ...stories.map((s) => ({ ...s, _type: "story" }))];
    // New show pinned first with "New Show!" callout; shuffle the rest
    const newShow = all.find((s) => s.id === NEW_SHOW_ID);
    const rest = all.filter((s) => s.id !== NEW_SHOW_ID).sort(() => 0.5 - Math.random()).slice(0, 4);
    return newShow ? [newShow, ...rest] : rest.slice(0, 5);
  }, [shows, stories]);

  /* ─────────────────────────────────────────────
     SUB-COMPONENTS
     ───────────────────────────────────────────── */

  // Global flag: only enable hover videos after images have had time to load
  const [videosEnabled, setVideosEnabled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVideosEnabled(true), 5000); // 5s after mount
    return () => clearTimeout(t);
  }, []);

  /* ── Card (used for stories, storybooks, shows) ── */
  const Card = ({ thumb, title, meta, onClick, videoUrl, index = 0, badge, eager = false }) => {
    const [hovered, setHovered] = useState(false);
    const vRef = useRef(null);
    useEffect(() => {
      if (!vRef.current || !videosEnabled) return;
      if (hovered) vRef.current.play().catch(() => {});
      else { vRef.current.pause(); vRef.current.currentTime = 0; }
    }, [hovered, videosEnabled]);

    const gradients = [
      "linear-gradient(135deg,#0065F8,#00CAFF)", "linear-gradient(135deg,#7FC6FF,#E1EBFF)",
      "linear-gradient(135deg,#3EDC8F,#00CAFF)", "linear-gradient(135deg,#FFD76A,#FF6B9A)",
      "linear-gradient(135deg,#0065F8,#7FC6FF)", "linear-gradient(135deg,#00CAFF,#E6F7EE)",
    ];
    const hash = (title || "").split("").reduce((a, c) => { a = (a << 5) - a + c.charCodeAt(0); return a & a; }, 0);
    const fallback = gradients[Math.abs(hash) % gradients.length];

    return (
      <motion.article className="sv-card" variants={fadeUp} custom={index} onClick={onClick}
        onMouseEnter={() => videosEnabled && setHovered(true)} onMouseLeave={() => setHovered(false)}
        whileHover={{ y: -5 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
        <div className="sv-card-thumb">
          {thumb ? (
            <img src={thumb} alt={title} className="sv-card-img"
              loading={eager ? "eager" : "lazy"}
              style={{ opacity: 0, transition: "opacity 0.3s ease" }}
              onLoad={(e) => { e.target.style.opacity = 1; }}
              onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="sv-card-placeholder" style={{ background: fallback }}>
              {(title || "").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
            </div>
          )}
          {videoUrl && videosEnabled && (
            <video ref={vRef} src={videoUrl} className="sv-card-vid" muted loop playsInline preload="none"
              style={{ opacity: hovered ? 1 : 0 }} />
          )}
          {badge && <div className="sv-badge" style={{ background: "none", padding: 0 }}>{badge}</div>}
          <div className={`sv-card-hover-overlay${hovered ? " show" : ""}`}>
            <div className="sv-card-play-circle">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        </div>
        <div className="sv-card-body">
          <h3 className="sv-card-name">{title}</h3>
          <span className="sv-card-sub" style={meta ? undefined : { visibility: "hidden" }}>{meta || "\u00A0"}</span>
        </div>
      </motion.article>
    );
  };

  /* ── Section wrapper ── */
  const Section = ({ title, children }) => (
    <AnimatedSection className="sv-section">
      <motion.h2 className="sv-section-heading" variants={fadeUp}>
        {title}
      </motion.h2>
      {children}
    </AnimatedSection>
  );

  /* ── Horizontal row ── */
  const HorizontalRow = ({ title, items, renderCard, variableWidth, book }) => {
    if (!items.length) return null;

    const getSlotClass = (item) => {
      if (book) return "sv-book-slot";
      if (!variableWidth) return "sv-card-slot";
      const maxViews = Math.max(...items.map((s) => s.views || 0), 1);
      const ratio = (item.views || 0) / maxViews;
      return ratio >= 0.4 ? "sv-card-slot wide" : "sv-card-slot";
    };

    return (
      <Section title={title}>
        <ScrollRow>
          {items.map((item, i) => (
            <div key={item.id} className={getSlotClass(item)}>
              {renderCard(item, i)}
            </div>
          ))}
        </ScrollRow>
      </Section>
    );
  };

  const SkeletonRow = ({ book = false, cards = 6 }) => (
    <div className="sv-section" aria-hidden="true">
      <div className="sv-skel sv-skel-heading" />
      <div className="sv-scroll-row">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className={book ? "sv-book-slot" : "sv-card-slot"}>
            <div className={`sv-skel ${book ? "sv-skel-book" : "sv-skel-card"}`} />
          </div>
        ))}
      </div>
    </div>
  );

  const storyCard = (s, i, eager = false) => (
    <Card thumb={s.thumbnail_url} title={s.name} meta={`${s.views || 0} views`}
      onClick={() => handleStoryClick(s.id, s.share_token)} videoUrl={s.video_url || s.file_url} index={i} eager={eager} />
  );
  const eagerStoryCard = (s, i) => storyCard(s, i, true);

  const showCard = (s, i) => {
    const isFeatured = s.id === FEATURED_SHOW_ID;
    const premiumBadge = !isAuthenticated ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999, background: "rgba(0,101,248,0.85)", color: "#fff", fontSize: 11, fontWeight: 700, boxShadow: "0px 2px 0px rgba(11,26,51,0.15)" }}><svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg> Premium</span> : null;
    const movieBadge = isFeatured ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999, background: "#0065F8", color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "'Fredoka', sans-serif", letterSpacing: "0.04em", textTransform: "uppercase", boxShadow: "0px 2px 0px #073B8E" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}>movie</span>
        Pixley Movie
      </span>
    ) : null;
    return (
      <Card thumb={getThumb(s)} title={s.name} meta={isFeatured ? "Pixley Movie ✦" : `${s.episode_count ?? 0} episodes`}
        onClick={() => handleShowClick(s.id)} index={i}
        badge={movieBadge || premiumBadge} />
    );
  };

  const bookCard = (b, i) => {
    const title = b.title || "Untitled";
    const SPINE_COLORS = ["#E0567A", "#5B8DEF", "#3FB984", "#F2A33C", "#9B6BE0", "#E86F51", "#22B6C4", "#F25C9A"];
    const h = title.split("").reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
    const accent = SPINE_COLORS[Math.abs(h) % SPINE_COLORS.length];
    return (
      <motion.article
        className="sv-book"
        style={{ "--sv-accent": accent }}
        variants={fadeUp}
        custom={i}
        onClick={() => navigate(`/storybooks/${b.id}`)}
        whileHover={{ y: -6 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="sv-book-stage">
          <div className="sv-book-3d">
            {/* right fore-edge + bottom edge = the book's page thickness (true 3D faces) */}
            <div className="sv-book-edge sv-book-edge--right" aria-hidden="true" />
            <div className="sv-book-edge sv-book-edge--bottom" aria-hidden="true" />
            {/* front cover */}
            <div className="sv-book-cover">
              {b.thumbnail_url ? (
                <img
                  src={b.thumbnail_url}
                  alt={title}
                  className="sv-book-img"
                  loading="lazy"
                  onError={(e) => { e.target.style.visibility = "hidden"; }}
                />
              ) : (
                <div className="sv-book-img sv-book-img--ph" />
              )}
              <span className="sv-book-spine" aria-hidden="true" />
              <span className="sv-book-scrim" aria-hidden="true" />
              <h3 className="sv-book-title">{title}</h3>
            </div>
          </div>
        </div>
        <div className="sv-book-meta">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.5C10.5 5.5 8 5 4 5v12c4 0 6.5.5 8 1.5 1.5-1 4-1.5 8-1.5V5c-4 0-6.5.5-8 1.5zM12 6.5V18.5" />
          </svg>
          <span>{b.views ?? 0} views</span>
        </div>
      </motion.article>
    );
  };

  /* ── error only — no loading gate, page renders immediately ── */

  if (error) return (
    <div className="sv-container"><div className="sv-center-screen">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: "center" }}>
        <h1 style={{ fontSize: "1.4rem", marginBottom: 8, color: "#0B1A33" }}>Something went wrong</h1>
        <p style={{ color: "#334155", marginBottom: 20 }}>We couldn't load your content right now.</p>
        <button className="sv-retry" onClick={loadStoriverseData}>Try Again</button>
      </motion.div>
    </div></div>
  );

  const popular = pixleyMixes.top_views || [];
  const adventure = pixleyMixes.adventure || [];
  const mystery = pixleyMixes.mystery || [];
  const mixesReady = !loading && !mixesLoading;

  return (
    <div className="sv-container">
      {!isAuthenticated && <FloatingCta onSignup={() => navigate("/auth?mode=signup")} />}

      {/* Hero — show a skeleton in its place until core content arrives */}
      {heroItems.length > 0 ? (
        <HeroCarousel items={heroItems} onStoryClick={handleStoryClick} onShowClick={handleShowClick} getThumb={getThumb} />
      ) : (
        <div className="sv-hero-skel sv-skel" aria-hidden="true" />
      )}

      <div className="sv-body">
        {/* Search results take priority at the top when searching */}
        {searchQuery && (
          <Section title="Search Results">
            {stories.length > 0 ? (
              <ScrollRow>
                {stories.slice(0, 10).map((s, i) => <div key={s.id} className="sv-card-slot">{storyCard(s, i)}</div>)}
              </ScrollRow>
            ) : (
              <motion.div className="sv-empty-msg" variants={fadeUp}>
                <p>No stories found matching your search.</p>
                <span>Try different keywords</span>
              </motion.div>
            )}
          </Section>
        )}

        {/* Continue watching */}
        {isAuthenticated && <ContinuePlaybackSection apiClient={apiClient} />}

        {/* Pixley Originals */}
        {shows.length > 0 ? (
          <HorizontalRow title="Pixley Originals" items={shows} renderCard={showCard} />
        ) : (loading && !searchQuery) ? (
          <SkeletonRow />
        ) : null}

        {/* Normal feed (hidden while searching). Each section shows a skeleton in its
            own position until its data lands, so the page fills top-to-bottom in place
            instead of bottom-first (Tier-1 rows used to beat the Tier-2 rows above them). */}
        {!searchQuery && (
          <>
            {/* For You */}
            {forYouStories.length > 0 ? (
              <HorizontalRow title="For You" items={forYouStories} renderCard={storyCard} />
            ) : (isAuthenticated && forYouLoading) ? (
              <SkeletonRow />
            ) : null}

            {/* Trending */}
            {popular.length > 0 ? (
              <HorizontalRow title="Trending Now" items={popular} renderCard={eagerStoryCard} variableWidth />
            ) : !mixesReady ? (
              <SkeletonRow />
            ) : null}

            {/* Adventure */}
            {adventure.length > 0 ? (
              <HorizontalRow title="Adventure Stories" items={adventure} renderCard={storyCard} variableWidth />
            ) : !mixesReady ? (
              <SkeletonRow />
            ) : null}

            {/* Mystery */}
            {mystery.length > 0 ? (
              <HorizontalRow title="Mystery Stories" items={mystery} renderCard={storyCard} />
            ) : !mixesReady ? (
              <SkeletonRow />
            ) : null}
          </>
        )}

        {/* Public storybooks */}
        {publicStorybooks.length > 0 ? (
          <HorizontalRow title="Storybooks" items={publicStorybooks} renderCard={bookCard} book />
        ) : (loading && !searchQuery) ? (
          <SkeletonRow book />
        ) : null}
      </div>

      <AuthWall isOpen={showAuthWall} onClose={() => setShowAuthWall(false)} feature={authWallFeature} />
      <IOSAppBanner />
      {showSuggestionAlert && <StorySuggestionAlert onClose={() => setShowSuggestionAlert(false)} />}
      <HelpFooter />
    </div>
  );
};

export default Storyverse;
